import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql, Link } from 'gatsby';
import cloneDeep from 'lodash.clonedeep';
import {
  PageHeader,
  DecorativeLink,
  ExpTreeTableWithButtons,
  Section,
  DangerousWrapper,
  toLineItem,
  toSectionsURL
} from '@budget-monorepo/shared-ui';
import Layout from '../components/Layout';
import HelmetData from '../components/HelmetData';

class OperatingTransfers extends React.Component {
  constructor(props) {
    super(props);
    const { data } = this.props;
    const allData = this.getInnerData(data.content.governmentAreaTable);
    this.state = {
      ...allData
    };
  }

  /* Because operating transfers uses mapping to fetch the line item node data to render in the table on the
     page, we rely on a getInnerData function to create the correct data structure for the ExpTreeTable component.
     This function pulls the mapped line item data to the the first level of the table object (table.data) and creates
     a drillable link to the line item pages. */
  getInnerData = (table) => {
    const treeTable = cloneDeep(table);
    const data = treeTable.data.map((row) => {
      const rowCopy = cloneDeep(row);
      if (rowCopy.lineItemName && !rowCopy.lineItem) {
        const {
          governmentArea, governmentOrganization, govLevelThree, lineItemNumber
        } = rowCopy;
        const slug = toSectionsURL({
          href: '/appropriations/',
          sections: governmentArea === governmentOrganization ? [governmentArea, govLevelThree, lineItemNumber] : [governmentArea, governmentOrganization, govLevelThree, lineItemNumber]
        });
        /* Create a new field for the operating transfers that consists of the line item number as a drillable link
           to the line item page and the line item number. */
        /* eslint-disable jsx-a11y/anchor-is-valid */
        rowCopy.lineItem = (
          <div>
            <span className="emphasize">
              <Link to={slug}>{toLineItem(rowCopy.lineItemNumber)}</Link>
            </span>
            {' '}
            <span>{rowCopy.lineItemName}</span>
          </div>
        );
      }
      return({ ...rowCopy });
    });
    treeTable.data = data;
    return{
      treeTable,
      treeTableData: data
    };
  };

  render() {
    const { data } = this.props;
    const { content, site, download } = data;
    // eslint-disable-next-line camelcase
    const { modifiedTime, mg_mod_date, mg_mod_datetime } = content.parent;
    const layoutProps = {
      activeIndex: 1,
      above: () => <PageHeader title={content.title} category={content.budgetSection} />,
      pageTitle: content.title,
      dataTimeStamp: modifiedTime,
      buildTimeStamp: site.buildTime
    };
    const { treeTable, treeTableData } = this.state;
    const expandableTreeTableWithButtonsProps = {
      defaultExpLevel: 2,
      expTreeTableProps: {
        table: treeTable,
        data: treeTableData,
        pivotBy: ['governmentArea', 'governmentOrganization'],
        aggregateBy: ['nextBudgetaryRecommendation'],
        subComponent: 'lineItemLanguage',
        subIndex: 1
      },
      treeTableButtonsProps: {
        a11yTitle: content.title,
        headerLevel: 2,
        buttons: [{
          info: 'View the operating transfers grouped by line item.',
          text: 'View by line item',
          accessor: 'lineItem',
          expLevel: 1
        }, {
          info: 'View the operating transfers with line item language.',
          text: 'View line item language',
          accessor: 'lineItemLanguage',
          expLevel: 2
        }]
      }
    };
    // metadata for the page
    const metaTitle = `${content.budgetSection}: ${content.title}`;
    const metaDescription = 'A summary of all recommended operating transfers.';
    return(
      <Fragment>
        <HelmetData {...{
          metaTitle,
          metaDescription,
          metaType: 'dataset',
          mg_mod_date,
          mg_mod_datetime
        }}
        />
        <Layout {...layoutProps}>
          <DangerousWrapper rawHtml={content.summary} />
          <Section>
            <DecorativeLink
              /* Create a reference link to the outside sections summary file */
              href={download.publicURL}
              text={`Download ${content.title}`}
              showFileIcon
            />
          </Section>
          <ExpTreeTableWithButtons {...expandableTreeTableWithButtonsProps} />
        </Layout>
      </Fragment>
    );
  }
}

OperatingTransfers.propTypes = {
  data: PropTypes.object
};

export default OperatingTransfers;

export const query = graphql`
  query operatingTransfersJson {
    site {
      ...SiteMetadata
    }
    # Fetch the url of the operating transfer pdf file for an in page download link.
    # If the file name changes, the query here ( eq: "fy2020h1_tab6.pdf") will need to be updated.
    download: file(base: { eq: "fy2020h1_tab6.pdf" }) {
      base
      publicURL
    }
    # Fetch all of the data from the operating-transfers.json file node.
    content: pagesJson(fields: {slug: {eq: "/operating-transfers/"}}) {
      title
      budgetSection
      summary
      governmentAreaTable {
        headers {
          name
          type
          align
          value
        }
        # Retrieve all of the required data fields from mapped line item nodes.
        data: lineItems {
          lineItemNumber
          lineItemName
          lineItemLanguage
          governmentArea: govLevelOne
          governmentOrganization: govLevelTwo
          govLevelThree
          nextBudgetaryRecommendation: nextFYspending
        }
      }
      # Get the last modified timestamp for the file powering the page
      ...PagesJsonDataTimeStamp
    }
  }
`;
